import { lazy, Suspense } from 'react';
import ReturnToRedirect from '@/components/auth/ReturnToRedirect';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import MetaPixel from '@/components/analytics/MetaPixel';
import ScrollRestoration from '@/components/layout/ScrollRestoration';
import AppErrorBoundary from '@/components/layout/AppErrorBoundary';
import CookieConsentBanner from '@/components/analytics/CookieConsentBanner';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { CartProvider } from '@/contexts/CartContext';
import { AuthProvider } from '@/contexts/AuthContext';
const ChatWidget = lazy(() => import('@/components/chat/ChatWidget'));

const Index = lazy(() => import('./pages/Index'));
const Auth = lazy(() => import('./pages/Auth'));
const TournamentPortal = lazy(() => import('./pages/TournamentPortal'));
const TournamentProjector = lazy(() => import('./pages/TournamentProjector'));
const AdminTournaments = lazy(() => import('./pages/admin/AdminTournaments'));
const Circuit = lazy(() => import('./pages/Circuit'));
const PlayProgram = lazy(() => import('./pages/PlayProgram'));
const AdminProgram = lazy(() => import('./pages/admin/AdminProgram'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Catalog = lazy(() => import('./pages/Catalog'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const PaymentReturn = lazy(() => import('./pages/PaymentReturn'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Profile = lazy(() => import('./pages/Profile'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminCheckoutLog = lazy(() => import('./pages/admin/AdminCheckoutLog'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const Collection = lazy(() => import('./pages/Collection'));
const Decks = lazy(() => import('./pages/Decks'));
const DeckBuilder = lazy(() => import('./pages/DeckBuilder'));
const DeckAnalyzer = lazy(() => import('./pages/DeckAnalyzer'));
const Binders = lazy(() => import('./pages/Binders'));
const BinderView = lazy(() => import('./pages/BinderView'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const MarketStats = lazy(() => import('./pages/MarketStats'));
const AdminChatConfig = lazy(() => import('./pages/admin/AdminChatConfig'));
const Events = lazy(() => import('./pages/Events'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const CreateEvent = lazy(() => import('./pages/CreateEvent'));
const EditEvent = lazy(() => import('./pages/EditEvent'));
const MyEvents = lazy(() => import('./pages/MyEvents'));
const MFAVerify = lazy(() => import('./pages/MFAVerify'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminApiMonitor = lazy(() => import('./pages/admin/AdminApiMonitor'));
const AdminFeedback = lazy(() => import('./pages/admin/AdminFeedback'));
const AdminPos = lazy(() => import('./pages/admin/AdminPos'));
const AdminCashRegister = lazy(() => import('./pages/admin/AdminCashRegister'));
const AdminInventory = lazy(() => import('./pages/admin/AdminInventory'));
const AdminFinance = lazy(() => import('./pages/admin/AdminFinance'));
const AdminAccounting = lazy(() => import('./pages/admin/AdminAccounting'));
const AdminManuals = lazy(() => import('./pages/admin/AdminManuals'));
const StoreDashboard = lazy(() => import('./pages/store/StoreDashboard'));
const StoreProducts = lazy(() => import('./pages/store/StoreProducts'));
const StoreInventory = lazy(() => import('./pages/store/StoreInventory'));
const StoreSales = lazy(() => import('./pages/store/StoreSales'));
const Contact = lazy(() => import('./pages/Contact'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const DataRights = lazy(() => import('./pages/DataRights'));
const AdminDataRequests = lazy(() => import('./pages/admin/AdminDataRequests'));
const FAQ = lazy(() => import('./pages/FAQ'));
import { categoryLandings } from './content/categoryLandings';

const Guides = lazy(() => import('./pages/Guides'));
const GuideDetail = lazy(() => import('./pages/GuideDetail'));
const CategoryLanding = lazy(() => import('./pages/CategoryLanding'));
const CardSearchE2E = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/CardSearchE2E'))
  : null;
const CardSearchRetryE2E = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/CardSearchRetryE2E'))
  : null;

// Reuse fetched data briefly instead of refetching on every mount/focus;
// live screens (tournaments, caja) keep their own refetch/realtime.
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner position="top-center" />
          <BrowserRouter>
          <AppErrorBoundary>
            <ScrollRestoration />
            <MetaPixel />
            <CookieConsentBanner />
            <Suspense fallback={<Loading />}>
              <ReturnToRedirect />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/catalogo" element={<Catalog />} />
                {categoryLandings.map((landing) => (
                  <Route
                    key={landing.path}
                    path={landing.path}
                    element={<CategoryLanding config={landing} />}
                  />
                ))}
                <Route path="/producto/:slug" element={<ProductDetail />} />
                <Route path="/carrito" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/pago/retorno" element={<PaymentReturn />} />
                <Route path="/perfil" element={<Profile />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/coleccion" element={<Collection />} />
                <Route path="/decks" element={<Decks />} />
                <Route path="/decks/:id" element={<DeckBuilder />} />
                <Route path="/decks/:deckId/analisis" element={<DeckAnalyzer />} />
                <Route path="/analizador" element={<DeckAnalyzer />} />
                <Route path="/analizador-commander" element={<Navigate to="/analizador" replace />} />
                <Route path="/binders" element={<Binders />} />
                <Route path="/b/:slug" element={<BinderView />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/marketplace/estadisticas" element={<MarketStats />} />
                <Route path="/eventos" element={<Events />} />
                <Route path="/eventos/:id" element={<EventDetail />} />
                <Route path="/circuit" element={<Circuit />} />
                <Route path="/programa" element={<PlayProgram />} />
                <Route path="/programa/:slug" element={<PlayProgram />} />
                <Route path="/torneos/:id" element={<TournamentPortal />} />
                <Route path="/torneos/:id/proyector" element={<TournamentProjector />} />
                <Route path="/mfa-verify" element={<MFAVerify />} />
                <Route path="/eventos/crear" element={<CreateEvent />} />
                <Route path="/eventos/mis" element={<MyEvents />} />
                <Route path="/eventos/editar/:id" element={<EditEvent />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/productos" element={<AdminProducts />} />
                <Route path="/admin/pedidos" element={<AdminOrders />} />
                <Route path="/admin/checkout-log" element={<AdminCheckoutLog />} />
                <Route path="/admin/usuarios" element={<AdminUsers />} />
                <Route path="/admin/blog" element={<AdminBlog />} />
                <Route path="/admin/chat" element={<AdminChatConfig />} />
                <Route path="/admin/eventos" element={<Navigate to="/admin/torneos?tab=eventos" replace />} />
                <Route path="/admin/torneos" element={<AdminTournaments />} />
                <Route path="/admin/cerrar-evento" element={<Navigate to="/admin/torneos?tab=cerrar" replace />} />
                <Route path="/admin/programa" element={<AdminProgram />} />
                <Route path="/admin/configuracion" element={<AdminSettings />} />
                <Route path="/admin/api-monitor" element={<AdminApiMonitor />} />
                <Route path="/admin/feedback" element={<AdminFeedback />} />
                <Route path="/admin/pos" element={<AdminPos />} />
                <Route path="/admin/caja" element={<AdminCashRegister />} />
                <Route path="/admin/inventario" element={<AdminInventory />} />
                <Route path="/admin/finanzas" element={<AdminFinance />} />
                <Route path="/admin/contabilidad" element={<AdminAccounting />} />
                <Route path="/admin/manuales" element={<AdminManuals />} />
                <Route path="/admin/documentacion" element={<Navigate to="/admin/manuales?tab=operacion" replace />} />
                <Route path="/admin/documentacion/caja" element={<Navigate to="/admin/manuales?tab=vendedor" replace />} />
                <Route path="/admin/datos-personales" element={<AdminDataRequests />} />
                <Route path="/store" element={<StoreDashboard />} />
                <Route path="/store/productos" element={<StoreProducts />} />
                <Route path="/store/inventario" element={<StoreInventory />} />
                <Route path="/store/ventas" element={<StoreSales />} />
                <Route path="/contacto" element={<Contact />} />
                <Route path="/terminos" element={<Terms />} />
                <Route path="/privacidad" element={<Privacy />} />
                <Route path="/privacidad/derechos" element={<DataRights />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/guias" element={<Guides />} />
                <Route path="/guias/:slug" element={<GuideDetail />} />
                {CardSearchE2E && (
                  <Route path="/dev/e2e/card-search" element={<CardSearchE2E />} />
                )}
                {CardSearchRetryE2E && (
                  <Route path="/dev/e2e/card-search-retry" element={<CardSearchRetryE2E />} />
                )}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Suspense fallback={null}>
              <ChatWidget />
            </Suspense>
          </AppErrorBoundary>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
